import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../../services/api';
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpRight,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  CreditCard,
  ExternalLink,
  Layers3,
  Loader2,
  ReceiptText,
  RefreshCw,
  Search,
  Sparkles,
  X,
  XCircle,
  Eye,
  ShieldCheck
} from 'lucide-react';
import './TransactionHistory.scss';

// -------------------------------------------------------------------
// Helpers & Data Formatters
// -------------------------------------------------------------------
const TYPE_TEXT = {
  payment: 'Pago de Suscripción',
  refund: 'Reembolso',
  subscription_created: 'Suscripción Creada',
  subscription_updated: 'Suscripción Renovada',
  subscription_canceled: 'Suscripción Cancelada',
  offer_activated: 'Collab Activado',
  paid_collab_payment: 'Pago de Paid Collab',
  paid_collab_refund: 'Reembolso de Paid Collab',
  paid_collab_position_adjustment: 'Ajuste de posiciones de Paid Collab',
};

const POSITION_ADJUSTMENT_EVENTS = {
  requested: 'Reducción solicitada',
  approved: 'Reducción aprobada',
  rejected: 'Reducción rechazada',
  processing: 'Reembolso en proceso',
  completed: 'Posiciones reducidas y reembolso confirmado',
  failed: 'Reembolso no completado',
};

const isPositionAdjustment = (transaction) => (
  transaction.transaction_type === 'paid_collab_position_adjustment'
);

const getStripeDetails = (t) => {
  const details = t.details || {};
  const paymentMetadata = t.payment?.metadata || {};
  const subscription = t.subscription || t.payment?.subscription || {};
  const paidCollab = t.paid_collab || {};

  return {
    provider: details.provider || paymentMetadata.provider || 'Stripe',
    subscriptionId: details.stripe_subscription_id || paymentMetadata.stripe_subscription_id || subscription.stripe_subscription_id,
    customerId: details.stripe_customer_id || paymentMetadata.stripe_customer_id || subscription.stripe_customer_id,
    invoiceId: details.stripe_invoice_id || paymentMetadata.stripe_invoice_id,
    checkoutSessionId: details.stripe_checkout_session_id || paymentMetadata.stripe_checkout_session_id || paidCollab.stripe_checkout_session_id,
    status: details.stripe_status || paymentMetadata.stripe_status,
    collabTitle: paidCollab.collab_title || details.collab_title,
    collabId: paidCollab.collab_id || details.collab_id,
    paymentStatus: paidCollab.payment_status || details.stripe_status || details.payment_status,
    paymentIntentId: paidCollab.stripe_payment_intent_id || details.stripe_payment_intent_id,
    chargeId: paidCollab.stripe_charge_id || details.stripe_charge_id,
    refundId: details.stripe_refund_id,
    grossAmount: paidCollab.gross_amount,
    creatorTotal: paidCollab.creator_total,
    fothFeeAmount: paidCollab.foth_fee_amount,
    processingFee: paidCollab.processing_fee_charged,
    planName: details.plan_name || t.subscription?.plan?.name || t.payment?.plan?.name,
  };
};

const formatDateShort = (str) => {
  if (!str) return 'Sin fecha';
  const d = new Date(str);
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (str) => {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFullDate = (str) => {
  if (!str) return 'Fecha no disponible';
  const d = new Date(str);
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (amount) => {
  const value = Number(amount || 0);
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getStatusBadge = (t) => {
  const stripe = getStripeDetails(t);
  const type = t.transaction_type;
  const statusRaw = (
    isPositionAdjustment(t)
      ? t.details?.refund_request_status
      : stripe.paymentStatus || stripe.status || ''
  ).toLowerCase();

  if (isPositionAdjustment(t)) {
    if (['requested', 'under_review', 'approved_full', 'approved_partial', 'processing'].includes(statusRaw)) {
      return { label: 'Pendiente', tone: 'warning', icon: Clock };
    }
    if (['rejected', 'failed', 'canceled'].includes(statusRaw)) {
      return { label: statusRaw === 'rejected' ? 'Rechazado' : 'No completado', tone: 'danger', icon: XCircle };
    }
    return { label: 'Completado', tone: 'success', icon: CheckCircle2 };
  }

  if (type === 'refund' || type === 'paid_collab_refund' || statusRaw === 'refunded') {
    return { label: 'Reembolsado', tone: 'refunded', icon: RefreshCw };
  }
  if (statusRaw === 'paid' || statusRaw === 'succeeded' || statusRaw === 'complete' || type === 'payment' || type === 'paid_collab_payment') {
    return { label: 'Completado', tone: 'success', icon: CheckCircle2 };
  }
  if (statusRaw.includes('pending') || type === 'subscription_created') {
    return { label: 'Pendiente', tone: 'warning', icon: Clock };
  }
  if (statusRaw === 'failed' || type === 'subscription_canceled') {
    return { label: 'Cancelado', tone: 'danger', icon: XCircle };
  }
  return { label: 'Procesado', tone: 'neutral', icon: CheckCircle2 };
};

const getConceptInfo = (t) => {
  const stripe = getStripeDetails(t);
  const type = t.transaction_type;

  if (isPositionAdjustment(t)) {
    return {
      title: `${POSITION_ADJUSTMENT_EVENTS[t.details?.adjustment_event] || 'Ajuste de posiciones'}: ${stripe.collabTitle || 'Paid Collab'}`,
      category: 'Ajuste de posiciones',
      badge: 'Ajuste',
      icon: RefreshCw,
      collabId: stripe.collabId,
      isCollab: true,
      isAdjustment: true,
    };
  }

  if (type === 'paid_collab_payment' || stripe.collabTitle) {
    return {
      title: stripe.collabTitle || 'Colaboración Remunerada',
      category: 'Pago de Collab',
      badge: 'Paid Collab',
      icon: Sparkles,
      collabId: stripe.collabId,
      isCollab: true,
    };
  }

  if (type === 'paid_collab_refund') {
    return {
      title: stripe.collabTitle ? `Reembolso: ${stripe.collabTitle}` : 'Reembolso de Collab',
      category: 'Reembolso',
      badge: 'Reembolso',
      icon: RefreshCw,
      collabId: stripe.collabId,
      isRefund: true,
    };
  }

  if (type.startsWith('subscription') || stripe.planName) {
    return {
      title: stripe.planName || 'Plan de Suscripción FOTH',
      category: 'Suscripción FOTH',
      badge: 'Membresía',
      icon: Layers3,
      isSubscription: true,
    };
  }

  if (type === 'refund') {
    return {
      title: 'Reembolso de Transacción',
      category: 'Reembolso',
      badge: 'Reembolso',
      icon: RefreshCw,
      isRefund: true,
    };
  }

  return {
    title: TYPE_TEXT[type] || 'Transacción FOTH',
    category: 'Movimiento de cuenta',
    badge: 'Pago',
    icon: CreditCard,
  };
};

// -------------------------------------------------------------------
// Main Component
// -------------------------------------------------------------------
const TransactionHistory = () => {
  const { isAuthenticated } = useAuth();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTx, setSelectedTx] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');

  // ---- Fetch ----
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get('/transactions/');
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.results ?? [];
        setTransactions(data);
      } catch {
        setError('No se pudieron cargar tus transacciones. Por favor, inténtalo de nuevo más tarde.');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [isAuthenticated]);

  // ---- Summary KPIs ----
  const stats = useMemo(() => {
    let totalInvested = 0;
    let paidCollabsCount = 0;
    let subscriptionsCount = 0;
    let refundsCount = 0;

    transactions.forEach((tx) => {
      const amount = Number(tx.amount || 0);
      const isRefund = ['refund', 'paid_collab_refund'].includes(tx.transaction_type);
      const isAdjustment = isPositionAdjustment(tx);

      if (isRefund) {
        refundsCount += 1;
      } else if (!isAdjustment) {
        totalInvested += amount;
        if (tx.transaction_type === 'paid_collab_payment' || tx.paid_collab?.collab_title) {
          paidCollabsCount += 1;
        } else if (tx.transaction_type?.startsWith('subscription') || tx.details?.plan_name) {
          subscriptionsCount += 1;
        }
      }
    });

    return {
      totalInvested,
      paidCollabsCount,
      subscriptionsCount,
      refundsCount,
      totalCount: transactions.length,
    };
  }, [transactions]);

  // ---- Filter Logic ----
  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      // Tab filter
      if (activeTab === 'collabs') {
        const isCollab = t.transaction_type === 'paid_collab_payment' || Boolean(t.paid_collab?.collab_title) || Boolean(t.details?.collab_title);
        if (!isCollab) return false;
      } else if (activeTab === 'subscriptions') {
        const isSub = t.transaction_type?.startsWith('subscription') || Boolean(t.details?.plan_name);
        if (!isSub) return false;
      } else if (activeTab === 'refunds') {
        const isRefund = ['refund', 'paid_collab_refund'].includes(t.transaction_type);
        if (!isRefund) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const statusBadge = getStatusBadge(t);
        if (statusFilter === 'completed' && statusBadge.tone !== 'success') return false;
        if (statusFilter === 'pending' && statusBadge.tone !== 'warning') return false;
        if (statusFilter === 'refunded' && statusBadge.tone !== 'refunded') return false;
      }

      // Search query
      if (searchTerm) {
        const q = searchTerm.toLowerCase().trim();
        const concept = getConceptInfo(t);
        const stripe = getStripeDetails(t);
        const rawType = TYPE_TEXT[t.transaction_type] || t.transaction_type;

        const matches =
          concept.title.toLowerCase().includes(q) ||
          concept.category.toLowerCase().includes(q) ||
          rawType.toLowerCase().includes(q) ||
          String(t.amount || '').includes(q) ||
          (stripe.paymentIntentId && stripe.paymentIntentId.toLowerCase().includes(q)) ||
          (stripe.chargeId && stripe.chargeId.toLowerCase().includes(q)) ||
          (stripe.collabId && String(stripe.collabId).toLowerCase().includes(q));

        if (!matches) return false;
      }

      return true;
    });
  }, [transactions, activeTab, statusFilter, searchTerm]);

  // ---- Copy to Clipboard helper ----
  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(''), 2000);
  };

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="transactions-page">
        <div className="transactions-shell">
          <div className="transactions-loading-state">
            <Loader2 className="spinning" size={32} />
            <h3>Cargando historial financiero...</h3>
            <p>Sincronizando tus transacciones y recibos con Stripe.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      <div className="transactions-shell">

        {/* Header / Hero */}
        <header className="transactions-header">
          <div className="header-left">
            <div className="header-eyebrow">
              <ReceiptText size={14} /> Facturación y Pagos
            </div>
            <h1 className="header-title">Historial de Transacciones</h1>
            <p className="header-subtitle">
              Gestiona los pagos de colaboraciones remuneradas, recibos de suscripción y reembolsos de tu empresa.
            </p>
          </div>
          <div className="header-actions">
            <Link to="/business/subscriptions" className="back-link-btn">
              <ArrowLeft size={16} /> Volver a Suscripciones
            </Link>
          </div>
        </header>

        {/* Error Alert */}
        {error && (
          <div className="alert-banner error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Executive KPI Cards */}
        <section className="metrics-grid">
          <div className="metric-card primary">
            <div className="metric-icon-wrap gold">
              <CreditCard size={20} />
            </div>
            <div className="metric-info">
              <span className="metric-label">Total Invertido</span>
              <h2 className="metric-value">{formatMoney(stats.totalInvested)}</h2>
              <span className="metric-subtext">En colaboraciones y suscripciones</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon-wrap emerald">
              <Sparkles size={20} />
            </div>
            <div className="metric-info">
              <span className="metric-label">Collabs Remunerados</span>
              <h2 className="metric-value">{stats.paidCollabsCount}</h2>
              <span className="metric-subtext">Campañas procesadas con éxito</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon-wrap violet">
              <Layers3 size={20} />
            </div>
            <div className="metric-info">
              <span className="metric-label">Planes y Membresías</span>
              <h2 className="metric-value">{stats.subscriptionsCount}</h2>
              <span className="metric-subtext">Pagos de suscripción registrados</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon-wrap neutral">
              <RefreshCw size={20} />
            </div>
            <div className="metric-info">
              <span className="metric-label">Reembolsos</span>
              <h2 className="metric-value">{stats.refundsCount}</h2>
              <span className="metric-subtext">Ajustes o cancelaciones</span>
            </div>
          </div>
        </section>

        {/* Controls Toolbar: Segmented Tabs & Search */}
        <section className="toolbar-section">
          <div className="tabs-segmented">
            <button
              type="button"
              className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              Todos <span className="tab-badge">{transactions.length}</span>
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'collabs' ? 'active' : ''}`}
              onClick={() => setActiveTab('collabs')}
            >
              <Sparkles size={14} /> Collabs Pagados
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'subscriptions' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscriptions')}
            >
              <Layers3 size={14} /> Suscripciones
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'refunds' ? 'active' : ''}`}
              onClick={() => setActiveTab('refunds')}
            >
              <RefreshCw size={14} /> Reembolsos
            </button>
          </div>

          <div className="toolbar-right">
            <div className="search-input-box">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por collab, ID o plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpiar búsqueda"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="select-dropdown-box">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filtrar por estado"
              >
                <option value="all">Todos los estados</option>
                <option value="completed">Completados</option>
                <option value="pending">Pendientes</option>
                <option value="refunded">Reembolsados</option>
              </select>
            </div>
          </div>
        </section>

        {/* Table Container */}
        <section className="table-card">
          {filtered.length === 0 ? (
            <div className="empty-state-wrap">
              <div className="empty-icon-circle">
                <ReceiptText size={28} />
              </div>
              <h3>No se encontraron transacciones</h3>
              <p>
                {searchTerm || activeTab !== 'all' || statusFilter !== 'all'
                  ? 'No hay movimientos que coincidan con los filtros y búsqueda aplicados.'
                  : 'Aún no registras movimientos o pagos en tu cuenta de negocio.'}
              </p>
              {(searchTerm || activeTab !== 'all' || statusFilter !== 'all') && (
                <button
                  type="button"
                  className="reset-filters-btn"
                  onClick={() => {
                    setActiveTab('all');
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="modern-transactions-table">
                <thead>
                  <tr>
                    <th style={{ width: '16%' }}>FECHA</th>
                    <th style={{ width: '38%' }}>CONCEPTO / COLABORACIÓN</th>
                    <th style={{ width: '12%' }}>MÉTODO</th>
                    <th style={{ width: '14%' }}>ESTADO</th>
                    <th style={{ width: '12%', textAlign: 'right' }}>MONTO</th>
                    <th style={{ width: '8%', textAlign: 'center' }}>DETALLE</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const concept = getConceptInfo(t);
                    const status = getStatusBadge(t);
                    const isRefund = ['refund', 'paid_collab_refund'].includes(t.transaction_type);
                    const isAdjustment = isPositionAdjustment(t);
                    const StatusIcon = status.icon;
                    const ConceptIcon = concept.icon;

                    return (
                      <tr
                        key={t.id}
                        className="table-row-interactive"
                        onClick={() => setSelectedTx(t)}
                      >
                        {/* Fecha */}
                        <td className="date-col">
                          <div className="date-primary">{formatDateShort(t.created_at)}</div>
                          <div className="date-secondary">{formatTime(t.created_at)}</div>
                        </td>

                        {/* Concepto */}
                        <td className="concept-col">
                          <div className="concept-wrapper">
                            <div className={`concept-icon-pill ${concept.isCollab ? 'collab' : concept.isSubscription ? 'sub' : 'refund'}`}>
                              <ConceptIcon size={16} />
                            </div>
                            <div className="concept-text">
                              <span className="concept-title" title={concept.title}>
                                {concept.title}
                              </span>
                              <span className="concept-subtitle">
                                {concept.category}
                                {concept.collabId && ` • ID #${concept.collabId.slice(0, 8)}`}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Método */}
                        <td className="method-col">
                          <span className="payment-gateway-badge">
                            <ShieldCheck size={13} className="gateway-icon" /> Stripe
                          </span>
                        </td>

                        {/* Estado */}
                        <td className="status-col">
                          <span className={`status-pill-badge ${status.tone}`}>
                            <StatusIcon size={13} />
                            {status.label}
                          </span>
                        </td>

                        {/* Monto */}
                        <td className="amount-col">
                          <span className={`amount-value ${isRefund ? 'positive' : isAdjustment ? '' : 'debit'}`}>
                            {isAdjustment ? '—' : isRefund ? `+ ${formatMoney(t.amount)}` : `- ${formatMoney(t.amount)}`}
                          </span>
                        </td>

                        {/* Acción */}
                        <td className="action-col" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="view-details-btn"
                            onClick={() => setSelectedTx(t)}
                            title="Ver comprobante y detalles"
                          >
                            <Eye size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Table Footer Count */}
          {filtered.length > 0 && (
            <div className="table-footer-bar">
              <span>Mostrando {filtered.length} de {transactions.length} transacciones registradas</span>
              <Link to="/business/plans" className="footer-plans-link">
                Ver Planes de Membresía <ArrowUpRight size={14} />
              </Link>
            </div>
          )}
        </section>

        {/* Transaction Receipt Modal / Drawer */}
        {selectedTx && (() => {
          const concept = getConceptInfo(selectedTx);
          const status = getStatusBadge(selectedTx);
          const stripe = getStripeDetails(selectedTx);
          const isRefund = ['refund', 'paid_collab_refund'].includes(selectedTx.transaction_type);
          const isAdjustment = isPositionAdjustment(selectedTx);
          const StatusIcon = status.icon;

          return (
            <div className="tx-modal-overlay" onClick={() => setSelectedTx(null)}>
              <div
                className="tx-modal-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="modal-receipt-title"
              >
                {/* Modal Header */}
                <div className="modal-header">
                  <div className="modal-header-left">
                    <div className="receipt-tag">
                      <ReceiptText size={14} /> Comprobante de Transacción
                    </div>
                    <h2 id="modal-receipt-title" className="modal-title">
                      {concept.title}
                    </h2>
                  </div>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => setSelectedTx(null)}
                    aria-label="Cerrar recibo"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Modal Hero Banner */}
                <div className="modal-hero-banner">
                  <div className="modal-hero-amount">
                    <span className="currency-label">{isAdjustment ? 'Movimiento de historial' : 'Monto Total'}</span>
                    <h1 className={`amount-large ${isRefund ? 'positive' : isAdjustment ? '' : 'debit'}`}>
                      {isAdjustment ? '—' : isRefund ? `+ ${formatMoney(selectedTx.amount)}` : `- ${formatMoney(selectedTx.amount)}`}
                    </h1>
                  </div>
                  <div className={`status-pill-badge large ${status.tone}`}>
                    <StatusIcon size={15} /> {status.label}
                  </div>
                </div>

                {/* Modal Body */}
                <div className="modal-body-content">
                  
                  {/* Financial Breakdown (for Paid Collabs) */}
                  {(stripe.creatorTotal || stripe.fothFeeAmount || stripe.processingFee) && (
                    <div className="receipt-breakdown-section">
                      <h4 className="section-mini-heading">Desglose de Pago</h4>
                      <div className="breakdown-list">
                        {stripe.creatorTotal && (
                          <div className="breakdown-row">
                            <span>Pago a Creadores</span>
                            <strong>{formatMoney(stripe.creatorTotal)}</strong>
                          </div>
                        )}
                        {stripe.fothFeeAmount && (
                          <div className="breakdown-row">
                            <span>Tarifa de plataforma FOTH</span>
                            <strong>{formatMoney(stripe.fothFeeAmount)}</strong>
                          </div>
                        )}
                        {stripe.processingFee && (
                          <div className="breakdown-row">
                            <span>Comisión de procesamiento</span>
                            <strong>{formatMoney(stripe.processingFee)}</strong>
                          </div>
                        )}
                        <div className="breakdown-row total">
                          <span>Total cobrado</span>
                          <span>{formatMoney(selectedTx.amount)} USD</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {isAdjustment && (
                    <div className="receipt-breakdown-section">
                      <h4 className="section-mini-heading">Detalle del ajuste</h4>
                      <div className="breakdown-list">
                        <div className="breakdown-row"><span>Posiciones</span><strong>{selectedTx.details?.previous_quantity} → {selectedTx.details?.requested_quantity}</strong></div>
                        <div className="breakdown-row"><span>Compensación a devolver</span><strong>{formatMoney(selectedTx.details?.creator_compensation_amount)}</strong></div>
                        <div className="breakdown-row"><span>Fee FOTH devuelto</span><strong>{formatMoney(selectedTx.details?.foth_fee_refund_amount)}</strong></div>
                        <div className="breakdown-row"><span>Reserva de payout devuelta</span><strong>{formatMoney(selectedTx.details?.payout_fee_reserve_amount)}</strong></div>
                        {selectedTx.details?.final_refund_amount && <div className="breakdown-row total"><span>Reembolso confirmado</span><span>{formatMoney(selectedTx.details.final_refund_amount)} USD</span></div>}
                      </div>
                    </div>
                  )}

                  {/* General Meta Information */}
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Fecha y Hora</span>
                      <span className="info-value">{formatFullDate(selectedTx.created_at)}</span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Tipo de Operación</span>
                      <span className="info-value">{TYPE_TEXT[selectedTx.transaction_type] || selectedTx.transaction_type}</span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Pasarela de Pago</span>
                      <span className="info-value secure-pill">
                        <ShieldCheck size={14} /> Stripe Payments
                      </span>
                    </div>

                    {stripe.collabId && (
                      <div className="info-item">
                        <span className="info-label">Colaboración</span>
                        <Link
                          to={`/business/collabs/${stripe.collabId}/summary`}
                          className="collab-link-pill"
                        >
                          Ver Campaña <ExternalLink size={13} />
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Reference Technical IDs (Organized with click-to-copy buttons) */}
                  <div className="technical-ids-box">
                    <h4 className="section-mini-heading">Identificadores de Referencia</h4>

                    {/* Transaction ID */}
                    <div className="id-row">
                      <div className="id-info">
                        <span className="id-type">ID de Transacción FOTH:</span>
                        <code className="id-code">tx_{selectedTx.id}</code>
                      </div>
                      <button
                        type="button"
                        className="copy-icon-btn"
                        onClick={() => handleCopy(`tx_${selectedTx.id}`, 'tx_id')}
                        title="Copiar ID"
                      >
                        {copiedKey === 'tx_id' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                        <span>{copiedKey === 'tx_id' ? 'Copiado' : 'Copiar'}</span>
                      </button>
                    </div>

                    {/* PaymentIntent */}
                    {stripe.paymentIntentId && (
                      <div className="id-row">
                        <div className="id-info">
                          <span className="id-type">Stripe PaymentIntent:</span>
                          <code className="id-code">{stripe.paymentIntentId}</code>
                        </div>
                        <button
                          type="button"
                          className="copy-icon-btn"
                          onClick={() => handleCopy(stripe.paymentIntentId, 'pi_id')}
                          title="Copiar ID"
                        >
                          {copiedKey === 'pi_id' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                          <span>{copiedKey === 'pi_id' ? 'Copiado' : 'Copiar'}</span>
                        </button>
                      </div>
                    )}

                    {/* Charge ID */}
                    {stripe.chargeId && (
                      <div className="id-row">
                        <div className="id-info">
                          <span className="id-type">Stripe Charge:</span>
                          <code className="id-code">{stripe.chargeId}</code>
                        </div>
                        <button
                          type="button"
                          className="copy-icon-btn"
                          onClick={() => handleCopy(stripe.chargeId, 'ch_id')}
                          title="Copiar ID"
                        >
                          {copiedKey === 'ch_id' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                          <span>{copiedKey === 'ch_id' ? 'Copiado' : 'Copiar'}</span>
                        </button>
                      </div>
                    )}

                    {/* Invoice ID */}
                    {stripe.invoiceId && (
                      <div className="id-row">
                        <div className="id-info">
                          <span className="id-type">Factura Stripe:</span>
                          <code className="id-code">{stripe.invoiceId}</code>
                        </div>
                        <button
                          type="button"
                          className="copy-icon-btn"
                          onClick={() => handleCopy(stripe.invoiceId, 'inv_id')}
                          title="Copiar ID"
                        >
                          {copiedKey === 'inv_id' ? <Check size={14} className="copied" /> : <Copy size={14} />}
                          <span>{copiedKey === 'inv_id' ? 'Copiado' : 'Copiar'}</span>
                        </button>
                      </div>
                    )}
                  </div>

                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    className="modal-action-btn close"
                    onClick={() => setSelectedTx(null)}
                  >
                    Cerrar Comprobante
                  </button>
                  {stripe.collabId && (
                    <Link
                      to={`/business/collabs/${stripe.collabId}/summary`}
                      className="modal-action-btn primary"
                    >
                      Ir a la Colaboración <ExternalLink size={15} />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
};

export default TransactionHistory;
